import React from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import LockIcon from "@mui/icons-material/Lock";
import PublicIcon from "@mui/icons-material/Public";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  InputAdornment,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useRecoilValue } from "recoil";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import organizationIdState from "../../../../atoms/organizationIdAtom";
import { normalizeFirestoreDate } from "../../../../lib/firestoreDate";

import { withGuidedSearchPreserve } from "./trainingPlansV2GuidedTaskParams";

import {
  appendRevision,
  comparePlanVersions,
  createVersionDraft,
  deleteArchivedPlanPermanently,
  getAssignableClients,
  listPlanAssignmentHistory,
  getPlanAssignmentCoverage,
  getPlanById,
  getPublishImpactPreview,
  getPlanUsage,
  listVersionFamily,
  publishVersionDraft,
  restoreVersionAsDraft,
  savePlanMetadata,
  setPlanStatus,
  trackTrainingPlansV2Event,
} from "./services/trainingPlansV2Service";
import { normalizePlanLifecycleStatus } from "./services/planLifecycleStatus";
import { useTrainingPlansV2Hints } from "./useTrainingPlansV2Hints";
import useCoachConsoleDocumentIdsVisible from "../../../../hooks/useCoachConsoleDocumentIdsVisible";

const ASSIGNMENT_HISTORY_PAGE_SIZE = 20;

const getCoverageChipSx = (count, activeColor) =>
  count > 0
    ? (theme) => ({
        color: theme.palette[activeColor].main,
        borderColor: theme.palette[activeColor].main,
        backgroundColor: alpha(theme.palette[activeColor].main, 0.12),
        "& .MuiChip-label": { fontWeight: 600 },
      })
    : undefined;

const TrainingPlansV2Manage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { planId } = useParams();
  const organizationId = useRecoilValue(organizationIdState);
  const hints = useTrainingPlansV2Hints("manage");
  const showDocumentIds = useCoachConsoleDocumentIdsVisible();
  const [plan, setPlan] = React.useState(null);
  const [usage, setUsage] = React.useState([]);
  const [clients, setClients] = React.useState([]);
  const [status, setStatus] = React.useState("active");
  const [summary, setSummary] = React.useState("");
  const [message, setMessage] = React.useState("");
  const [versions, setVersions] = React.useState([]);
  const [compareFromPlanId, setCompareFromPlanId] = React.useState("");
  const [compareToPlanId, setCompareToPlanId] = React.useState("");
  const [compareResult, setCompareResult] = React.useState(null);
  const [loadingCompare, setLoadingCompare] = React.useState(false);
  const [coverageByPlanId, setCoverageByPlanId] = React.useState({});
  const [assignmentHistory, setAssignmentHistory] = React.useState([]);
  const [assignmentHistoryLastDoc, setAssignmentHistoryLastDoc] = React.useState(null);
  const [assignmentHistoryHasMore, setAssignmentHistoryHasMore] = React.useState(false);
  const [loadingAssignmentHistory, setLoadingAssignmentHistory] = React.useState(false);
  const [expandedAssignmentNotes, setExpandedAssignmentNotes] = React.useState({});
  const [urlCopied, setUrlCopied] = React.useState(false);
  const getVersionNumber = React.useCallback((value) => value?.v2Meta?.versionNumber ?? 0, []);

  const load = React.useCallback(async () => {
    const [planValue, usageValue, familyValue, clientsValue, assignmentHistoryValue] = await Promise.all([
      getPlanById(planId),
      getPlanUsage(planId),
      listVersionFamily({ planId, limit: 200 }),
      getAssignableClients(),
      listPlanAssignmentHistory({
        planId,
        pageSize: ASSIGNMENT_HISTORY_PAGE_SIZE,
      }),
    ]);
    setPlan(planValue);
    setUsage(usageValue || []);
    setClients(clientsValue || []);
    setStatus(planValue?.v2Meta?.status || "active");
    const nextVersions = familyValue?.versions || [];
    setVersions(nextVersions);
    if (!compareToPlanId && nextVersions.length > 0) {
      const latest = [...nextVersions].sort(
        (a, b) => getVersionNumber(b) - getVersionNumber(a),
      )[0];
      setCompareToPlanId(latest?.id || "");
    }
    if (!compareFromPlanId && nextVersions.length > 1) {
      const ordered = [...nextVersions].sort(
        (a, b) => getVersionNumber(b) - getVersionNumber(a),
      );
      setCompareFromPlanId(ordered[1]?.id || ordered[0]?.id || "");
    }
    setAssignmentHistory(assignmentHistoryValue?.assignments || []);
    setAssignmentHistoryLastDoc(assignmentHistoryValue?.lastDoc || null);
    setAssignmentHistoryHasMore(!!assignmentHistoryValue?.hasMore);
    setExpandedAssignmentNotes({});
  }, [compareFromPlanId, compareToPlanId, getVersionNumber, planId]);

  React.useEffect(() => {
    load();
  }, [load]);

  React.useEffect(() => {
    async function loadCoverage() {
      const planIds = versions.map((version) => version.id).filter(Boolean);
      if (!planIds.length || !clients.length) {
        setCoverageByPlanId({});
        return;
      }
      const coverage = await getPlanAssignmentCoverage({
        planIds,
        clients,
      });
      setCoverageByPlanId(coverage || {});
    }
    loadCoverage();
  }, [clients, versions]);

  const handleSaveStatus = async () => {
    try {
      const currentStatus = plan?.v2Meta?.status || plan?.status || "active";
      if (currentStatus === "draft" && status === "active") {
        const preview = await getPublishImpactPreview({ draftPlanId: planId });
        const shouldContinue = window.confirm(
          `Publish this draft version?\n\nPrevious active version: ${
            preview?.previousActiveVersion
              ? `V${preview.previousActiveVersion?.v2Meta?.versionNumber ?? "?"}`
              : "None detected"
          }\nEstimated primary client migrations: ${
            preview?.migratedClientCountEstimate || 0
          }\n\nAlternative assignments are not migrated by publish.`,
        );
        if (!shouldContinue) {
          return;
        }
        const result = await setPlanStatus(plan, status, {
          summary: summary.trim() || "Published via status change",
        });
        await trackTrainingPlansV2Event("plan_version_publish", {
          planId,
          migratedClientCount: result?.migratedClientCount || 0,
        });
        setSummary("");
        setMessage(
          `Version published. Migrated ${
            result?.migratedClientCount || 0
          } primary client assignment(s). Alternative assignments were unchanged.`,
        );
        await load();
        return;
      }
      await setPlanStatus(plan, status);
      await appendRevision(planId, {
        action: "status_change",
        summary: `Status changed to ${status}`,
      });
      await trackTrainingPlansV2Event("plan_status_change", {
        planId,
        status,
      });
      setMessage("Plan metadata saved.");
      load();
    } catch (error) {
      setMessage(error?.message || "Failed to update plan status.");
    }
  };

  const handleCreateVersionDraft = async (sourcePlanId = planId) => {
    const created = await createVersionDraft({
      sourcePlanId,
      summary: summary.trim(),
    });
    await trackTrainingPlansV2Event("plan_version_draft_create", {
      planId: sourcePlanId,
      createdPlanId: created?.id,
    });
    setSummary("");
    setMessage(`Version draft V${created?.v2Meta?.versionNumber ?? "?"} created.`);
    navigate(
      withGuidedSearchPreserve(`/home/training-plans-v2/${created.id}/edit`, location.search),
    );
  };

  const handleRestoreAsDraft = async (sourceVersionPlanId) => {
    const shouldContinue = window.confirm(
      "Restore this version? A new version number will be created and you can edit it from the editor.",
    );
    if (!shouldContinue) {
      return;
    }
    const created = await restoreVersionAsDraft({
      sourceVersionPlanId,
      summary: summary.trim(),
    });
    await trackTrainingPlansV2Event("plan_version_restore_as_draft", {
      planId: sourceVersionPlanId,
      createdPlanId: created?.id,
    });
    setSummary("");
    const restoredStatus = created?.v2Meta?.status || "draft";
    const statusLabel = restoredStatus === "active" ? "active (edit and assign)" : "draft";
    setMessage(
      `Restored as V${created?.v2Meta?.versionNumber ?? "?"} — ${statusLabel}.`,
    );
    navigate(
      withGuidedSearchPreserve(`/home/training-plans-v2/${created.id}/edit`, location.search),
    );
  };

  const handlePublishDraft = async () => {
    const preview = await getPublishImpactPreview({ draftPlanId: planId });
    const shouldContinue = window.confirm(
      `Publish this draft version?\n\nPrevious active version: ${
        preview?.previousActiveVersion
          ? `V${preview.previousActiveVersion?.v2Meta?.versionNumber ?? "?"}`
          : "None detected"
      }\nEstimated primary client migrations: ${
        preview?.migratedClientCountEstimate || 0
      }\n\nAlternative assignments are not migrated by publish.`,
    );
    if (!shouldContinue) {
      return;
    }
    const result = await publishVersionDraft({
      draftPlanId: planId,
      summary: summary.trim(),
    });
    await trackTrainingPlansV2Event("plan_version_publish", {
      planId,
      migratedClientCount: result?.migratedClientCount || 0,
    });
    setSummary("");
    setMessage(
      `Version published. Migrated ${
        result?.migratedClientCount || 0
      } primary client assignment(s). Alternative assignments were unchanged.`,
    );
    await load();
  };

  const handleMarkArchived = async (version) => {
    await setPlanStatus(version, "archived");
    setMessage(`Archived V${version?.v2Meta?.versionNumber ?? "?"}.`);
    await load();
  };

  const handleDeleteArchivedVersion = async (version) => {
    const versionStatus = normalizePlanLifecycleStatus(
      version?.v2Meta?.status || version?.status || "active",
    );
    if (versionStatus !== "archived") {
      setMessage("Only archived versions can be deleted permanently.");
      return;
    }
    const cov = coverageByPlanId[version.id] || { primaryCount: 0, alternativeCount: 0 };
    if ((cov.primaryCount || 0) + (cov.alternativeCount || 0) > 0) {
      setMessage(
        "Cannot delete this version while it is assigned to clients. Change primary or alternative assignments first.",
      );
      return;
    }
    const ok = window.confirm(
      `Permanently delete V${version?.v2Meta?.versionNumber ?? "?"} (${version.name || "Untitled"})? This cannot be undone.`,
    );
    if (!ok) {
      return;
    }
    try {
      await deleteArchivedPlanPermanently(version.id);
      await trackTrainingPlansV2Event("plan_delete_archived_permanent", { planId: version.id });
      if (version.id === planId) {
        setMessage("Plan deleted.");
        navigate(withGuidedSearchPreserve("/home/training-plans-v2", location.search));
        return;
      }
      setMessage(`Deleted V${version?.v2Meta?.versionNumber ?? "?"}.`);
      await load();
    } catch (err) {
      if (err?.code === "PROTOCOL_ASSIGNED") {
        setMessage("Cannot delete: this version is still assigned to one or more clients.");
        return;
      }
      if (err?.code === "PROTOCOL_NOT_ARCHIVED") {
        setMessage(err.message);
        return;
      }
      setMessage(err?.message || "Delete failed.");
    }
  };

  const handleCompareVersions = async () => {
    if (!compareFromPlanId || !compareToPlanId) {
      setMessage("Select both versions to compare.");
      return;
    }
    setLoadingCompare(true);
    try {
      const diff = await comparePlanVersions({
        fromPlanId: compareFromPlanId,
        toPlanId: compareToPlanId,
      });
      setCompareResult(diff);
    } finally {
      setLoadingCompare(false);
    }
  };

  const handleLoadMoreAssignmentHistory = async () => {
    if (!assignmentHistoryHasMore || !assignmentHistoryLastDoc || loadingAssignmentHistory) {
      return;
    }
    setLoadingAssignmentHistory(true);
    try {
      const result = await listPlanAssignmentHistory({
        planId,
        pageSize: ASSIGNMENT_HISTORY_PAGE_SIZE,
        startAfterDoc: assignmentHistoryLastDoc,
      });
      setAssignmentHistory((prev) => [...prev, ...(result?.assignments || [])]);
      setAssignmentHistoryLastDoc(result?.lastDoc || null);
      setAssignmentHistoryHasMore(!!result?.hasMore);
    } finally {
      setLoadingAssignmentHistory(false);
    }
  };

  const orderedVersions = React.useMemo(
    () => [...versions].sort((a, b) => getVersionNumber(b) - getVersionNumber(a)),
    [getVersionNumber, versions],
  );

  const clientsById = React.useMemo(() => {
    return clients.reduce((acc, client) => {
      const key = client?.uid || client?.id;
      if (key) {
        acc[key] = client;
      }
      return acc;
    }, {});
  }, [clients]);

  const currentActiveVersion = React.useMemo(
    () =>
      orderedVersions.find((version) => (version?.v2Meta?.status || "active") === "active") || null,
    [orderedVersions],
  );

  const currentPlanCoverage = coverageByPlanId[planId] || {
    primaryCount: usage.length,
    alternativeCount: 0,
  };
  const currentLifecycleStatus = plan?.v2Meta?.status || status || "active";
  const isReadOnlyLifecycleStatus = currentLifecycleStatus === "archived";
  const isAssignedPlanLocked =
    (currentPlanCoverage.primaryCount || 0) > 0 || (currentPlanCoverage.alternativeCount || 0) > 0;
  const formatDiffValue = React.useCallback((value) => {
    if (value === null || value === undefined || value === "") {
      return "—";
    }
    if (typeof value === "object") {
      try {
        return JSON.stringify(value);
      } catch (error) {
        return String(value);
      }
    }
    return String(value);
  }, []);
  const formatAssignmentDate = React.useCallback((value) => {
    const date = normalizeFirestoreDate(value);
    if (!date) {
      return "Unknown date";
    }
    return date.toLocaleString();
  }, []);

  const persistedLifecycleStatus = plan?.v2Meta?.status || plan?.status || "active";
  const saveStatusButtonLabel = React.useMemo(() => {
    if (persistedLifecycleStatus === "draft" && status === "active") {
      return "Publish & activate";
    }
    return "Save status";
  }, [persistedLifecycleStatus, status]);

  const planPrivacy = plan?.privacy || "private";
  const isPublic = planPrivacy === "public";
  const publicPlanUrl = React.useMemo(() => {
    if (!organizationId || !planId) return "";
    return `${window.location.origin}/plan/${organizationId}/${planId}`;
  }, [organizationId, planId]);

  const handleTogglePlanPrivacy = async () => {
    const nextPrivacy = isPublic ? "private" : "public";
    if (nextPrivacy === "public") {
      const shouldContinue = window.confirm(
        "Make this plan publicly accessible? Anyone with the link will be able to view it without signing in.",
      );
      if (!shouldContinue) return;
    }
    try {
      await savePlanMetadata(planId, { privacy: nextPrivacy });
      await appendRevision(planId, {
        action: "privacy_change",
        summary: `Plan set to ${nextPrivacy}`,
      });
      await trackTrainingPlansV2Event("plan_privacy_change", { planId, privacy: nextPrivacy });
      setMessage(`Plan is now ${nextPrivacy}.`);
      await load();
    } catch (error) {
      setMessage(error?.message || "Failed to update plan privacy.");
    }
  };

  const handleCopyPlanUrl = async () => {
    try {
      await navigator.clipboard.writeText(publicPlanUrl);
      setUrlCopied(true);
      setTimeout(() => setUrlCopied(false), 2500);
    } catch {
      alert(`Copy this URL: ${publicPlanUrl}`);
    }
  };

  return (
    <Box sx={{ pb: { xs: 10, md: 14 } }}>
      {message ? (
        <Alert sx={{ mb: 2 }} severity="info" onClose={() => setMessage("")}>
          {message}
        </Alert>
      ) : null}

      <Typography variant="h6">Manage Training Plan</Typography>
      <Typography color="text.secondary" sx={{ mb: 1 }}>
        {plan?.name || "Loading..."}{" "}
        {plan?.v2Meta?.versionNumber !== undefined && plan?.v2Meta?.versionNumber !== null
          ? `• V${plan.v2Meta.versionNumber}`
          : ""}
      </Typography>

      <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center" sx={{ mb: 2 }}>
        <Chip size="small" label={`Saved status: ${persistedLifecycleStatus}`} variant="outlined" />
        <Button
          variant="outlined"
          size="small"
          onClick={() =>
            navigate(
              withGuidedSearchPreserve(
                `/home/training-plans-v2/${planId}/edit`,
                location.search,
              ),
            )
          }
        >
          Edit plan
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={() =>
            navigate(
              withGuidedSearchPreserve(
                `/home/training-plans-v2/${planId}/assign`,
                location.search,
              ),
            )
          }
        >
          Assign
        </Button>
        {hints.hasAnyDismissedInScope ? (
          <Button variant="text" size="small" onClick={hints.restoreAll}>
            Show hints
          </Button>
        ) : null}
      </Stack>

      {!hints.isDismissed("manage_page_organized") ? (
      <Alert severity="info" sx={{ mb: 3 }} onClose={() => hints.dismiss("manage_page_organized")}>
        <Typography variant="subtitle2" component="div" sx={{ fontWeight: 700, mb: 0.5 }}>
          How this page is organized
        </Typography>
        <Box component="ul" sx={{ m: 0, pl: 2.5, mb: 0 }}>
          <li>
            <strong>Updates &amp; versions</strong> — create a new draft, publish, and browse all
            versions in the family.
          </li>
          <li>
            <strong>Assign</strong> — use the <strong>Assign</strong> button in the Training Plans toolbar
            (above, next to Edit and Manage) to see who has this version, add assignments, or{" "}
            <strong>remove an alternative</strong> (under <em>Who has this plan</em> →{" "}
            <strong>Remove alternative</strong>; primary stays unchanged).
          </li>
          <li>
            <strong>Lifecycle status</strong> — a label on this version. If the saved version is a{" "}
            <strong>draft</strong>, setting the dropdown to <strong>Active</strong> and saving runs the
            same publish flow as <strong>Publish This Draft</strong> (confirms migration of primary
            clients). It does not replace assigning clients from the toolbar.
          </li>
        </Box>
      </Alert>
      ) : null}

      {!hints.isDismissed("manage_assignment_counts") ? (
      <Alert severity="info" sx={{ mb: 3 }} onClose={() => hints.dismiss("manage_assignment_counts")}>
        <strong>Assignment counts</strong> (this version): Primary {currentPlanCoverage.primaryCount || 0}{" "}
        • Alternative {currentPlanCoverage.alternativeCount || 0}. Use the{" "}
        <Button
          size="small"
          variant="text"
          sx={{ verticalAlign: "baseline", p: 0, minWidth: 0, fontWeight: 700 }}
          onClick={() =>
            navigate(
              withGuidedSearchPreserve(
                `/home/training-plans-v2/${planId}/assign`,
                location.search,
              ),
            )
          }
        >
          Assign
        </Button>{" "}
        button in the Training Plans toolbar for the full roster and changes.
      </Alert>
      ) : null}

      <Stack spacing={3}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography fontWeight={700}>1. Updates &amp; versions</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1.5 }}>
            Start here when you are changing workouts or rolling out a new version.
          </Typography>
          {!hints.isDismissed("manage_publish_migrates") ? (
          <Alert
            severity="info"
            sx={{ mb: 2 }}
            onClose={() => hints.dismiss("manage_publish_migrates")}
          >
            Publishing updates lifecycle status and migrates <strong>primary</strong> assignments from the
            previous active version. Alternative assignments are not migrated.
          </Alert>
          ) : null}
          <Stack direction="row" spacing={1} sx={{ mt: 1, mb: 2, alignItems: "flex-start" }} flexWrap="wrap">
            <TextField
              size="small"
              label="Version summary note (optional)"
              value={summary}
              onChange={(e) => setSummary(e.target.value)}
              sx={{ flex: 1, minWidth: 280 }}
            />
            <Button
              variant="contained"
              onClick={() => handleCreateVersionDraft(planId)}
              sx={{ whiteSpace: "nowrap" }}
            >
              Create version draft
            </Button>
            {persistedLifecycleStatus === "draft" ? (
              <Button
                variant="outlined"
                onClick={handlePublishDraft}
                sx={{ whiteSpace: "nowrap" }}
              >
                Publish this draft
              </Button>
            ) : null}
          </Stack>

          <Typography fontWeight={700} sx={{ mb: 1 }}>
            Suggested next step
          </Typography>
          <Stack spacing={1} sx={{ mb: 2 }}>
            {persistedLifecycleStatus === "draft" && !hints.isDismissed("manage_next_draft") ? (
              <Alert severity="warning" onClose={() => hints.dismiss("manage_next_draft")}>
                This version is still a draft. Use <strong>Publish this draft</strong> (or lifecycle{" "}
                <strong>Active</strong> + save in section 2) when you are ready for it to become the active
                version for rollout.
              </Alert>
            ) : null}
            {persistedLifecycleStatus === "active" &&
            (currentPlanCoverage.primaryCount || 0) === 0 &&
            !hints.isDismissed("manage_next_no_primary") ? (
              <Alert severity="info" onClose={() => hints.dismiss("manage_next_no_primary")}>
                This version is active in metadata but has no primary client assignments yet. Use the{" "}
                <strong>Assign</strong> button in the Training Plans toolbar to roll it out.
              </Alert>
            ) : null}
            {currentActiveVersion && currentActiveVersion.id !== planId && !hints.isDismissed("manage_next_other_active") ? (
              <Alert severity="warning" onClose={() => hints.dismiss("manage_next_other_active")}>
                A different version (V{currentActiveVersion?.v2Meta?.versionNumber ?? "?"}) is currently
                marked active.
              </Alert>
            ) : null}
            {isReadOnlyLifecycleStatus && !hints.isDismissed("manage_next_readonly") ? (
              <Alert severity="info" onClose={() => hints.dismiss("manage_next_readonly")}>
                This version is read-only. Use <strong>Restore as draft</strong> in the timeline below or
                create a new version draft.
              </Alert>
            ) : null}
          </Stack>

          <Typography fontWeight={700} sx={{ mb: 1 }}>
            Version timeline
          </Typography>
          {orderedVersions.length === 0 ? (
            <Typography color="text.secondary">No versions yet.</Typography>
          ) : (
            <List dense>
              {orderedVersions.map((version) => {
                const versionStatus = normalizePlanLifecycleStatus(
                  version?.v2Meta?.status || version?.status || "active",
                );
                const versionCoverage = coverageByPlanId[version.id] || {
                  primaryCount: 0,
                  alternativeCount: 0,
                };
                const isSupersededActive =
                  versionStatus === "active" &&
                  !!currentActiveVersion &&
                  currentActiveVersion.id !== version.id;
                return (
                  <ListItem key={version.id}>
                    <ListItemText
                      primary={`V${version?.v2Meta?.versionNumber ?? "?"} • ${
                        version.name || "Untitled"
                      }`}
                      secondary={`${(
                        normalizeFirestoreDate(version?.v2Meta?.versionCreatedOn) ??
                          normalizeFirestoreDate(version?.createdOn) ??
                          new Date()
                      ).toLocaleString()}${
                        showDocumentIds ? ` • ${version.id}` : ""
                      }`}
                    />
                    <ListItemSecondaryAction>
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        <Chip size="small" label={`Status: ${versionStatus}`} />
                        <Chip
                          size="small"
                          variant="outlined"
                          sx={getCoverageChipSx(versionCoverage.primaryCount || 0, "success")}
                          label={`Primary: ${versionCoverage.primaryCount || 0}`}
                        />
                        <Chip
                          size="small"
                          variant="outlined"
                          sx={getCoverageChipSx(
                            versionCoverage.alternativeCount || 0,
                            "warning",
                          )}
                          label={`Alternative: ${versionCoverage.alternativeCount || 0}`}
                        />
                        {isSupersededActive ? (
                          <Chip size="small" color="warning" label="Superseded active" />
                        ) : null}
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() =>
                            navigate(
                              withGuidedSearchPreserve(
                                `/home/training-plans-v2/${version.id}/edit`,
                                location.search,
                              ),
                            )
                          }
                        >
                          Open
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleRestoreAsDraft(version.id)}
                        >
                          Restore as draft
                        </Button>
                        {versionStatus === "archived" ? (
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            onClick={() => handleDeleteArchivedVersion(version)}
                          >
                            Delete permanently
                          </Button>
                        ) : null}
                        {isSupersededActive ? (
                          <Button
                            size="small"
                            variant="outlined"
                            color="warning"
                            onClick={() => handleMarkArchived(version)}
                          >
                            Archive
                          </Button>
                        ) : null}
                      </Stack>
                    </ListItemSecondaryAction>
                  </ListItem>
                );
              })}
            </List>
          )}
        </Paper>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography fontWeight={700}>2. Lifecycle status</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 1 }}>
            Labels this version in the family. It is not the same as assigning clients to the plan.
          </Typography>
          {persistedLifecycleStatus === "draft" && !hints.isDismissed("manage_lifecycle_draft_explain") ? (
            <Alert
              severity="warning"
              sx={{ mb: 1.5 }}
              onClose={() => hints.dismiss("manage_lifecycle_draft_explain")}
            >
              This version is saved as a <strong>draft</strong>. If you set status to{" "}
              <strong>Active</strong> and save, you get the same publish confirmation as{" "}
              <strong>Publish this draft</strong> — primary clients migrate from the previous active
              version.
            </Alert>
          ) : null}
          {persistedLifecycleStatus !== "draft" && !hints.isDismissed("manage_lifecycle_active_explain") ? (
            <Alert
              severity="info"
              sx={{ mb: 1.5 }}
              onClose={() => hints.dismiss("manage_lifecycle_active_explain")}
            >
              Choosing <strong>Active</strong> and saving only publishes when the <em>saved</em> version is
              a draft. For an already-active version, save moves between active and archived without
              re-running publish.
            </Alert>
          ) : null}
          <Stack direction="row" spacing={2} sx={{ mt: 1 }} flexWrap="wrap" alignItems="center">
            <Select size="small" value={status} onChange={(e) => setStatus(e.target.value)}>
              <MenuItem value="active" disabled={isReadOnlyLifecycleStatus}>
                Active
              </MenuItem>
              <MenuItem value="draft" disabled={isAssignedPlanLocked || isReadOnlyLifecycleStatus}>
                Draft
              </MenuItem>
              <MenuItem value="archived" disabled={isAssignedPlanLocked}>
                Archived
              </MenuItem>
            </Select>
            <Button variant="contained" onClick={handleSaveStatus}>
              {saveStatusButtonLabel}
            </Button>
          </Stack>
          {isAssignedPlanLocked ? (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
              Assigned plans are locked to active status. Create a new version draft to make changes.
            </Typography>
          ) : null}
          {isReadOnlyLifecycleStatus ? (
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
              Archived versions are read-only. Use Restore as draft (timeline) or Create version draft to
              make updates.
            </Typography>
          ) : null}
        </Paper>

        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
            <Typography fontWeight={700}>3. Share plan</Typography>
            {isPublic ? (
              <Chip size="small" icon={<PublicIcon />} label="Public" color="success" variant="outlined" />
            ) : (
              <Chip size="small" icon={<LockIcon />} label="Private" variant="outlined" />
            )}
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {isPublic
              ? "This plan is publicly accessible. Anyone with the link can view it without signing in."
              : "This plan is private. Make it public to share a read-only view with anyone."}
          </Typography>
          <Stack spacing={1.5}>
            <Box>
              <Button
                variant={isPublic ? "outlined" : "contained"}
                size="small"
                startIcon={isPublic ? <LockIcon /> : <PublicIcon />}
                onClick={handleTogglePlanPrivacy}
              >
                {isPublic ? "Make private" : "Make public & share"}
              </Button>
            </Box>
            {isPublic ? (
              <TextField
                size="small"
                label="Share URL"
                value={publicPlanUrl}
                inputProps={{ readOnly: true }}
                sx={{ maxWidth: 560 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title={urlCopied ? "Copied!" : "Copy link"}>
                        <Button
                          size="small"
                          startIcon={<ContentCopyIcon fontSize="small" />}
                          onClick={handleCopyPlanUrl}
                        >
                          {urlCopied ? "Copied" : "Copy"}
                        </Button>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
            ) : null}
          </Stack>
        </Paper>

        <Accordion defaultExpanded={false} disableGutters elevation={0} sx={{ border: 1, borderColor: "divider", borderRadius: 1, "&:before": { display: "none" } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2, py: 1.5 }}>
            <Box>
              <Typography fontWeight={700}>Assignment history</Typography>
              <Typography variant="body2" color="text.secondary">
                Recent assignment events (tracked from this rollout forward)
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 2, pb: 2, pt: 0 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Earlier assignments are not shown.
            </Typography>
            {assignmentHistory.length === 0 ? (
              <Typography color="text.secondary">
                No assignment history recorded for this plan yet.
              </Typography>
            ) : (
              <Stack spacing={1}>
                <List dense>
                  {assignmentHistory.map((event) => {
                    const clientLookup =
                      clientsById[event?.clientId] || clientsById[event?.clientUID] || null;
                    const clientDisplayName =
                      event?.clientNameSnapshot ||
                      `${clientLookup?.firstName || ""} ${clientLookup?.lastName || ""}`.trim() ||
                      event?.clientId ||
                      "Unknown client";
                    const modeLabel =
                      event?.mode === "alternative"
                        ? "Alternative assignment"
                        : event?.mode === "variant_draft"
                          ? "Variant draft (primary unchanged)"
                          : event?.createVariant
                            ? "Primary assignment (variant)"
                            : "Primary assignment";
                    const note = typeof event?.coachNote === "string" ? event.coachNote.trim() : "";
                    const isExpanded = !!expandedAssignmentNotes[event.id];
                    const notePreview =
                      note.length > 180 && !isExpanded ? `${note.slice(0, 180)}...` : note;
                    return (
                      <ListItem key={`assignment-history-${event.id}`} alignItems="flex-start">
                        <ListItemText
                          primary={`${clientDisplayName} • ${modeLabel}`}
                          secondary={`${formatAssignmentDate(event?.assignedOn)} • Coach: ${event?.actor || "Unknown"}`}
                        />
                        <Stack spacing={0.5} sx={{ minWidth: 260, maxWidth: 420 }}>
                          <Typography variant="caption" color="text.secondary">
                            Note
                          </Typography>
                          <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                            {notePreview || "No note provided"}
                          </Typography>
                          {note.length > 180 ? (
                            <Button
                              size="small"
                              variant="text"
                              onClick={() =>
                                setExpandedAssignmentNotes((prev) => ({
                                  ...prev,
                                  [event.id]: !prev[event.id],
                                }))
                              }
                            >
                              {isExpanded ? "Hide full note" : "Show full note"}
                            </Button>
                          ) : null}
                        </Stack>
                      </ListItem>
                    );
                  })}
                </List>
                {assignmentHistoryHasMore ? (
                  <Box>
                    <Button
                      variant="outlined"
                      onClick={handleLoadMoreAssignmentHistory}
                      disabled={loadingAssignmentHistory}
                    >
                      {loadingAssignmentHistory ? "Loading..." : "Load more history"}
                    </Button>
                  </Box>
                ) : null}
              </Stack>
            )}
          </AccordionDetails>
        </Accordion>

        <Accordion defaultExpanded={false} disableGutters elevation={0} sx={{ border: 1, borderColor: "divider", borderRadius: 1, "&:before": { display: "none" } }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2, py: 1.5 }}>
            <Box>
              <Typography fontWeight={700}>Compare versions</Typography>
              <Typography variant="body2" color="text.secondary">
                Diff metadata and workouts between two saved versions
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails sx={{ px: 2, pb: 2, pt: 0 }}>
            <Stack direction="row" spacing={1} sx={{ mt: 0, mb: 2 }} flexWrap="wrap">
              <FormFieldSelect
                label="From"
                value={compareFromPlanId}
                onChange={setCompareFromPlanId}
                versions={versions}
              />
              <FormFieldSelect
                label="To"
                value={compareToPlanId}
                onChange={setCompareToPlanId}
                versions={versions}
              />
              <Button variant="contained" onClick={handleCompareVersions} disabled={loadingCompare}>
                Compare
              </Button>
            </Stack>
            {compareResult ? (
              <Stack spacing={1}>
                <Typography variant="body2">
                  Metadata changes: {compareResult.summary.metadataChanges} • Workouts added:{" "}
                  {compareResult.summary.workoutsAdded} • Workouts removed:{" "}
                  {compareResult.summary.workoutsRemoved}
                </Typography>
                <Typography variant="body2">
                  Exercise added: {compareResult.summary.exerciseAdded} • Exercise removed:{" "}
                  {compareResult.summary.exerciseRemoved} • Order changed:{" "}
                  {compareResult.summary.workoutOrderChanged ? "Yes" : "No"}
                </Typography>
                <Divider />
                <Box>
                  <Typography variant="subtitle2">Metadata Field Changes</Typography>
                  {compareResult.details?.metadataChanges?.length ? (
                    <List dense sx={{ mt: 0.5 }}>
                      {compareResult.details.metadataChanges.map((change) => (
                        <ListItem key={`meta-${change.field}`}>
                          <ListItemText
                            primary={change.field}
                            secondary={`From: ${formatDiffValue(change.before)} → To: ${formatDiffValue(
                              change.after,
                            )}`}
                          />
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      No metadata changes.
                    </Typography>
                  )}
                </Box>
                <Box>
                  <Typography variant="subtitle2">Workout Structure Changes</Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 0.5, mb: 1 }}>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`Order changed: ${compareResult.details?.workouts?.orderChanged ? "Yes" : "No"}`}
                    />
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`Added: ${compareResult.details?.workouts?.added?.length || 0}`}
                    />
                    <Chip
                      size="small"
                      variant="outlined"
                      label={`Removed: ${compareResult.details?.workouts?.removed?.length || 0}`}
                    />
                  </Stack>
                  {compareResult.details?.workouts?.added?.length ? (
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      Added workouts: {compareResult.details.workouts.added.join(", ")}
                    </Typography>
                  ) : null}
                  {compareResult.details?.workouts?.removed?.length ? (
                    <Typography variant="body2">
                      Removed workouts: {compareResult.details.workouts.removed.join(", ")}
                    </Typography>
                  ) : null}
                  {!compareResult.details?.workouts?.added?.length &&
                  !compareResult.details?.workouts?.removed?.length &&
                  !compareResult.details?.workouts?.orderChanged ? (
                    <Typography variant="body2" color="text.secondary">
                      No workout structure changes.
                    </Typography>
                  ) : null}
                </Box>
                <Box>
                  <Typography variant="subtitle2">Exercise Changes by Workout</Typography>
                  {compareResult.details?.exercisesByWorkout?.length ? (
                    <List dense sx={{ mt: 0.5 }}>
                      {compareResult.details.exercisesByWorkout.map((change) => (
                        <ListItem key={`exercise-${change.workout}`}>
                          <ListItemText
                            primary={change.workout}
                            secondary={`Added: ${change.added || 0} • Removed: ${change.removed || 0}`}
                          />
                        </ListItem>
                      ))}
                    </List>
                  ) : (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      No exercise-level changes in shared workouts.
                    </Typography>
                  )}
                </Box>
              </Stack>
            ) : (
              <Typography color="text.secondary">Select two versions to see differences.</Typography>
            )}
          </AccordionDetails>
        </Accordion>
      </Stack>
    </Box>
  );
};

const FormFieldSelect = ({ label, value, onChange, versions }) => (
  <Select
    size="small"
    value={value}
    displayEmpty
    onChange={(event) => onChange(event.target.value)}
    sx={{ minWidth: 220 }}
  >
    <MenuItem value="">{label}</MenuItem>
    {[...versions]
      .sort((a, b) => (b?.v2Meta?.versionNumber ?? 0) - (a?.v2Meta?.versionNumber ?? 0))
      .map((version) => (
        <MenuItem key={`${label}-${version.id}`} value={version.id}>
          {`V${version?.v2Meta?.versionNumber ?? "?"} • ${version?.v2Meta?.status || "active"} • ${version.name || version.id}`}
        </MenuItem>
      ))}
  </Select>
);

export default TrainingPlansV2Manage;
